import React, { useCallback } from "react";
import { Backdrop, Box, CircularProgress, Typography } from "@mui/material";
import useDialogModal from "../../../../hooks/useDialogModal";
import { usePrint } from "../../../../hooks/usePrint";
import EyeExamDialog from "./EyeExamDialog";
import EyeExamEditDialog from "./EyeExamEditDialog";
import EnhancedTable from "../../../enhancedTable/EnhancedTable";
import PrintPreviewDialog from "../../../print/PrintPreviewDialog";
import { EyeExamService } from "../../../../service/eyeExamService";
import useAbortController from "../../../../hooks/useAbortController";
import { toast } from "react-toastify";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import PrintIcon from '@mui/icons-material/Print';
import { formatDateWithTimezone } from "../../../../utils";
import { useAuth } from "../../../../hooks/useAuth";
import { useCan } from "hooks/useCan";
import ConfirmationDialog from "../../../ui/confirmationDialog";
import validLookup from "../../../../utils/validLookup";

/**
 * EyeExamTab
 * - Lists a patient's eye exams (placeholder for now: in-memory list)
 * - Allows creating, editing, and deleting exams
 * - Opens EyeExamDialog for create/edit
 *
 * Props
 * - customer: { customer: {...}, familyMembers: [...] }
 */
export default function EyeExamTab({ customer }) {
  const [EyeExamDlg, openEyeExamDlg] = useDialogModal(EyeExamDialog);
  const [
    ConfirmationDialogComp,
    showConfirmationDialog,
    closeConfirmationDialog,
  ] = useDialogModal(ConfirmationDialog);
  const [signal] = useAbortController();
  const { user } = useAuth();
  const can = useCan();
  const canAddEyeExam = can.action('eyeexam', 'create');
  const canDeleteEyeExam = can.action('eyeexam', 'delete');
  const canVoidEyeExam = can.action('eyeexam', 'void');
  const { printPDF, previewUrl, closePreview, isGenerating: isPrinting, cleanup } = usePrint();

  const patient = customer?.customer;

  // Direct state management for edit dialog
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editData, setEditData] = React.useState(null);

  const [state, updateState] = React.useReducer((s, p) => ({ ...s, ...p }), {
    data: [],
    totalRowCount: 0,
    pageCount: 0,
    loading: false,
    selected: null,
  });

  const columns = React.useMemo(() => ([
    { Header: 'Exam Date', accessor: 'examDate', enableFilter: true, type: 'date' },
    { Header: 'Practitioner', accessor: 'practitionerName', enableFilter: false, Cell: ({ row }) => row.original?.practitioner?.fullName || '' },
    {
      Header: 'Chief Complaint',
      accessor: 'chiefComplaint',
      enableFilter: true,
      // Prevent long text from stretching the table; show full on hover.
      Cell: ({ value }) => (
        <Box sx={{ maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={value || ''}>
          {value}
        </Box>
      ),
      width: 320,
    },
    { Header: 'Updated', accessor: 'updatedAt', Cell: ({ value }) => formatDateWithTimezone(value, 'PP p') },
    { Header: 'Status', accessor: 'status' },
  ]), []);

  const fetchDataImpl = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
    const from = pageSize * pageIndex;
    updateState({ loading: true });
    EyeExamService
      .listWithLimit(from, pageSize, filters, sortBy, patient?.id, signal)
      .then((data) => {
        updateState({
          data: data.data,
          totalRowCount: data.count,
          pageCount: Math.ceil(data.count / pageSize),
          loading: false,
        });
      })
      .catch((err) => {
        updateState({ loading: false });
      });
  }, [patient?.id, signal]);

  const handleAdd = () => {
    openEyeExamDlg((evt) => {
      // After save, refresh list
      if (evt?.action === 'save') {
        // trigger EnhancedTable to refetch; easiest is to call fetchDataImpl for first page
        fetchDataImpl({ pageSize: 10, pageIndex: 0, filters: [], sortBy: [] });
      }
    }, { patient });
  };

  const handleEdit = async (row) => {
      const details = await EyeExamService.getById(row.id);
      setEditData(details);
      setEditDialogOpen(true);
  };

  const handlePrint = async (row) => {
    try {
      await printPDF(() => EyeExamService.printSummary(row.id));
    } catch (error) {
      toast.error('Failed to print eye exam summary');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => () => cleanup(), []);

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditData(null);
  };

  const handleEditDialogSave = (evt) => {
    if (evt?.action === 'save') {
      fetchDataImpl({ pageSize: 10, pageIndex: 0, filters: [], sortBy: [] });
    }
    handleEditDialogClose();
  };

  const handleVoid = (rows) => {
    showConfirmationDialog(async (action) => {
      if (action === validLookup.confirmationDialogAction.ok) {
        try {
          for (const r of rows) {
            await EyeExamService.void(r.id);
          }
          toast.success('Exam(s) voided');
          fetchDataImpl({ pageSize: 10, pageIndex: 0, filters: [], sortBy: [] });
        } catch (e) {
          // toast is handled by interceptor
        }
      }
      closeConfirmationDialog();
    }, {
      title: 'Void exams',
      message: `Please confirm if you would like to void ${rows.length} exam(s). Voided exams can be recovered.`,
    });
  };

  const handleDelete = (rows) => {
    showConfirmationDialog(async (action) => {
      if (action === validLookup.confirmationDialogAction.ok) {
        try {
          for (const r of rows) {
            await EyeExamService.delete(r.id);
          }
          toast.success('Exam(s) deleted');
          fetchDataImpl({ pageSize: 10, pageIndex: 0, filters: [], sortBy: [] });
        } catch (e) {
          // toast is handled by interceptor
        }
      }
      closeConfirmationDialog();
    }, {
      title: 'Delete exams',
      message: `Please confirm if you would like to delete ${rows.length} exam(s). Deleted exams and their associated payloads cannot be recovered.`,
    });
  };

  const selectedToolbarActions = React.useMemo(() => {
    const actions = [];
    if (canVoidEyeExam) {
      actions.push({
        id: 'void',
        label: 'Void',
        icon: <BlockIcon fontSize="small" />,
        variant: 'button',
        color: 'success',
        tooltip: 'Void selected exams',
        onClick: (rows, { clearSelection }) => {
          handleVoid(rows);
          clearSelection && clearSelection();
        }
      });
    }
    if (canDeleteEyeExam) {
      actions.push({
        id: 'delete',
        label: 'Delete',
        icon: <DeleteIcon fontSize="small" />,
        variant: 'button',
        color: 'error',
        tooltip: 'Delete selected exams',
        onClick: (rows, { clearSelection }) => {
          handleDelete(rows);
          clearSelection && clearSelection();
        }
      });
    }
    return actions;
  }, [canVoidEyeExam, canDeleteEyeExam]);

  return (
    <Box>
      <EnhancedTable
        columns={columns}
        addRecordHandler={canAddEyeExam ? handleAdd : null}
        // Use explicit toolbar actions for Void and Delete instead of default delete handler
        deleteRecordHandler={null}
        data={state.data}
        totalRowCount={state.totalRowCount}
        pageCount={state.pageCount}
        fetchData={fetchDataImpl}
        loading={state.loading}
        selectionMode="multi"
        selectedToolbarActions={selectedToolbarActions}
        actions={[
          { icon: <EditIcon />, onClick: handleEdit, tooltip: 'Edit' },
          {
            icon: <PrintIcon />,
            onClick: handlePrint,
            tooltip: 'Print eye exam summary',
            disabled: (row) => row.status !== 'final' || isPrinting
          }
        ]}
      />
      <EyeExamDlg ignoreBackdropClick patient={patient} practitionerId={user?.id} />
      <EyeExamEditDialog 
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        listener={handleEditDialogSave}
        patient={patient}
        examData={editData}
        practitionerId={user?.id}
      />
      <ConfirmationDialogComp
        label={{
          positive: "Confirm",
          negative: "Cancel",
        }}
      />
      <Backdrop
        open={isPrinting}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.modal + 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography>Preparing eye exam summary...</Typography>
      </Backdrop>
      <PrintPreviewDialog
        url={previewUrl}
        onClose={closePreview}
        title="Eye Exam Summary"
      />
    </Box>
  );
}
